/**
 * サインイン手順の状態機械
 *
 * Amplify の `signIn` / `confirmSignIn` を順に呼び、Cognito が返す次のステップを画面の状態へ
 * 写像する。デスクトップ向け（SignInPage）と Web 向け（WebLoginPage）で同じ手順を使う（要件 1.6）。
 *
 * パスワードとコードは state にもログにも残さない（送信の引数として受け取り、そのまま渡すだけ）。
 */

import { confirmSignIn, type SignInOutput, signIn } from 'aws-amplify/auth';
import { useCallback, useRef, useState } from 'react';
import { classifyAuthError, type SignInFlowError } from './classify-auth-error';

/** 認証アプリに表示される発行者名（本番） */
const TOTP_ISSUER_BASE = 'きゅーもAI エージェント';

/**
 * 認証アプリに表示される発行者名。
 *
 * dev・staging では環境名を付け、同じ認証アプリに並ぶ本番の登録と見分けられるようにする
 * （画面右上の環境バッジと同じ表記）。登録時の名前が認証アプリに残るため、変更は新しい登録から効く。
 */
export function getTotpIssuer(
  environment: string | undefined = import.meta.env.VITE_APP_ENVIRONMENT,
): string {
  return environment && environment !== 'prod'
    ? `${TOTP_ISSUER_BASE} (${environment.toUpperCase()})`
    : TOTP_ISSUER_BASE;
}

export type SignInStep =
  | { kind: 'credentials' }
  | { kind: 'newPassword' }
  | { kind: 'totpSetup'; setupUri: string; sharedSecret: string }
  | { kind: 'totpCode' }
  | { kind: 'setupComplete' }
  | { kind: 'done' };

export interface SignInFlow {
  step: SignInStep;
  error: SignInFlowError | null;
  submitting: boolean;
  submitCredentials(email: string, password: string): Promise<void>;
  submitNewPassword(newPassword: string): Promise<void>;
  submitTotpSetupCode(code: string): Promise<void>;
  submitTotpCode(code: string): Promise<void>;
  acknowledgeSetupComplete(): Promise<void>;
  /** 完了後の後処理が失敗したときに、後処理だけをやり直す（サインインはやり直さない） */
  retryCompletion(): Promise<void>;
  restart(): void;
}

export interface UseSignInFlowOptions {
  /** 完了時の後処理（デスクトップへのトークン受け渡し、または認証状態の更新） */
  onSignedIn: () => Promise<void>;
}

const CREDENTIALS: SignInStep = { kind: 'credentials' };

/** このエラーでは、同じサインインのセッションでは続けられないため資格情報の入力に戻す */
function requiresRestart(error: SignInFlowError): boolean {
  return error.kind === 'sessionExpired' || error.kind === 'tooManyAttempts';
}

export function useSignInFlow({ onSignedIn }: UseSignInFlowOptions): SignInFlow {
  const [step, setStep] = useState<SignInStep>(CREDENTIALS);
  const [error, setError] = useState<SignInFlowError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // 登録用 URI のアカウント名に使う。パスワードは保持しない
  const emailRef = useRef('');

  const finish = useCallback(async () => {
    setStep({ kind: 'done' });
    setError(null);
    try {
      await onSignedIn();
    } catch {
      setError({ kind: 'unknown' });
    }
  }, [onSignedIn]);

  /** Amplify の結果を次の段階へ写像する。`viaSetup` はこの応答が登録の確認に対するものか */
  const applyResult = useCallback(
    async (result: SignInOutput, viaSetup: boolean) => {
      const { nextStep } = result;
      switch (nextStep.signInStep) {
        case 'DONE':
          if (viaSetup) {
            setStep({ kind: 'setupComplete' });
            return;
          }
          await finish();
          return;
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
          setStep({ kind: 'newPassword' });
          return;
        case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
          const { totpSetupDetails } = nextStep;
          setStep({
            kind: 'totpSetup',
            setupUri: totpSetupDetails.getSetupUri(getTotpIssuer(), emailRef.current).toString(),
            sharedSecret: totpSetupDetails.sharedSecret,
          });
          return;
        }
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
          setStep({ kind: 'totpCode' });
          return;
        // 漏洩パスワードでのサインイン。Cognito の PasswordResetRequiredException を Amplify が
        // ステップに変換して返す。パスワード再設定を案内する（要件 6.3）
        case 'RESET_PASSWORD':
          setStep(CREDENTIALS);
          setError({ kind: 'compromisedCredentials' });
          return;
        // サインアップ後に確認コードを入力していないアカウント。アカウント作成からやり直すと
        // 確認コードが再送される（use-sign-up）ため、そちらへ案内する
        case 'CONFIRM_SIGN_UP':
          setStep(CREDENTIALS);
          setError({ kind: 'signUpNotConfirmed' });
          return;
        default:
          setStep(CREDENTIALS);
          setError({ kind: 'unsupported', step: nextStep.signInStep });
      }
    },
    [finish],
  );

  const run = useCallback(
    async (call: () => Promise<SignInOutput>, viaSetup: boolean) => {
      setSubmitting(true);
      setError(null);
      try {
        await applyResult(await call(), viaSetup);
      } catch (err) {
        const classified: SignInFlowError = classifyAuthError(err);
        setError(classified);
        if (requiresRestart(classified)) {
          setStep(CREDENTIALS);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [applyResult],
  );

  const submitCredentials = useCallback(
    async (email: string, password: string) => {
      emailRef.current = email;
      await run(
        () =>
          signIn({
            username: email,
            password,
            // SRP では漏洩パスワードの検知が働かないため、パスワード直接送信に統一する（要件 6.1）
            options: { authFlowType: 'USER_PASSWORD_AUTH' },
          }),
        false,
      );
    },
    [run],
  );

  const submitNewPassword = useCallback(
    async (newPassword: string) => {
      if (step.kind !== 'newPassword') return;
      await run(() => confirmSignIn({ challengeResponse: newPassword }), false);
    },
    [run, step.kind],
  );

  const submitTotpSetupCode = useCallback(
    async (code: string) => {
      if (step.kind !== 'totpSetup') return;
      await run(() => confirmSignIn({ challengeResponse: code }), true);
    },
    [run, step.kind],
  );

  const submitTotpCode = useCallback(
    async (code: string) => {
      if (step.kind !== 'totpCode') return;
      await run(() => confirmSignIn({ challengeResponse: code }), false);
    },
    [run, step.kind],
  );

  const acknowledgeSetupComplete = useCallback(async () => {
    if (step.kind !== 'setupComplete') return;
    await finish();
  }, [finish, step.kind]);

  const retryCompletion = useCallback(async () => {
    if (step.kind !== 'done') return;
    setSubmitting(true);
    try {
      await finish();
    } finally {
      setSubmitting(false);
    }
  }, [finish, step.kind]);

  const restart = useCallback(() => {
    setStep(CREDENTIALS);
    setError(null);
  }, []);

  return {
    step,
    error,
    submitting,
    submitCredentials,
    submitNewPassword,
    submitTotpSetupCode,
    submitTotpCode,
    acknowledgeSetupComplete,
    retryCompletion,
    restart,
  };
}
